import { ChevronDown } from 'lucide-react';
import { createContext, useContext, type ReactNode } from 'react';
import { useStoredState } from '../hooks/useStoredState';
import { useI18n } from '../i18n';

interface Props {
  /** localStorage key that remembers whether the card is open. */
  storageKey: string;
  icon: ReactNode;
  title: string;
  /** One-line summary shown under the title, so a closed card still says what it holds. */
  sub?: ReactNode;
  /** Small marker next to the title, e.g. a count of problems. */
  badge?: ReactNode;
  actions?: ReactNode;
  /** Show the header buttons only while the card is open. */
  hideClosedActions?: boolean;
  defaultOpen?: boolean;
  children: ReactNode;
}

/** Inside this provider every card is shown open, e.g. while a settings search is active. */
export const ForceOpen = createContext(false);

/** Card whose header folds the body away; the open state is remembered per key. */
export function CollapsibleCard({ storageKey, icon, title, sub, badge, actions, hideClosedActions, defaultOpen = true, children }: Props) {
  const { t } = useI18n();
  const [stored, setOpen] = useStoredState(storageKey, defaultOpen);
  const forced = useContext(ForceOpen);
  const open = forced || stored;
  return (
    <div className={`card ${open ? '' : 'card-closed'}`}>
      <div className="card-head">
        <button type="button" className="card-toggle" aria-expanded={open} onClick={() => !forced && setOpen(!open)} title={t(open ? 'common.collapse' : 'common.expand')}>
          <div className="tile">{icon}</div>
          <div className="grow">
            <h2 className="row gap-8">
              {title}
              {badge}
            </h2>
            {sub && (
              <div className="sub" title={typeof sub === 'string' ? sub : undefined}>
                {sub}
              </div>
            )}
          </div>
          <ChevronDown className={`chev ${open ? '' : 'closed'}`} />
        </button>
        {(open || !hideClosedActions) && actions}
      </div>
      {open && children}
    </div>
  );
}
